import { useEffect, useState } from "react";

import { mockTradeRates } from "../data/mockTradeRates";
import CurrencyFormatter from "../utils/currencyFormatter";

function getSafeTradeRate(assetId) {
  if (!assetId || typeof assetId !== "string") {
    return undefined;
  }

  const numericId = assetId.trim();
  if (!/^\d+$/.test(numericId)) {
    return undefined;
  }

  const entry = Object.entries(mockTradeRates).find(
    ([key]) => key === numericId
  );
  return entry ? entry[1] : undefined;
}

export function useTradeRate(assetId, direction, pairAsset, isPostTrade) {
  const [data, setData] = useState(null);
  const [loadingTradeRate, setLoading] = useState(true);
  const [tradeRateError, setError] = useState(null);

  useEffect(() => {
    setLoading(true);

    const timeout = setTimeout(() => {
      const rate = getSafeTradeRate(assetId);

      if (rate) {
        setData({
          rate,
          direction,
          pair: pairAsset,
        });
        setError(null);
      } else {
        setError("No rate found");
        setData(null);
      }

      setLoading(false);
    }, 300);

    return () => clearTimeout(timeout);
  }, [assetId, direction, pairAsset]);

  return { data, loadingTradeRate, tradeRateError };
}

export const customQuote = () => {
  const getQuote = (data) => {
    const {
      incomingAsset,
      outgoingAsset,
      amount,
      rate,
      tradeType,
      pair,
      isBaseInput,
    } = data;

    if (amount === null || amount === undefined) {
      throw new Error("Amount is required to generate a quote");
    }

    const parsedAmount =
      typeof amount === "string"
        ? parseFloat(amount.replace(/,/g, ""))
        : parseFloat(amount);

    if (isNaN(parsedAmount) || parsedAmount <= 0) {
      throw new Error("Please enter a valid amount greater than zero");
    }

    const feeDecimal = 0;
    const feePercentage = { $numberDecimal: feeDecimal.toString() };
    let totalAmount;
    const serviceFee = parsedAmount * feeDecimal;

    if (isBaseInput) {
      totalAmount = parsedAmount * rate + serviceFee;
    }
    if (!isBaseInput) {
      totalAmount = parsedAmount / rate + serviceFee;
    }

    const formatter = new CurrencyFormatter();
    const formatCurrency = (value, currency) => {
      const isZAR = currency.toLowerCase() === "zar";
      const symbol = isZAR ? "R" : "";
      return `${symbol} ${Number(value).toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`;
    };

    const formatAsset = (value, asset) => {
      return `${Number(value).toFixed(8)} ${asset?.toUpperCase()}`;
    };

    return {
      amountReceived: parsedAmount,
      formattedAmountReceived:
        tradeType === "buy"
          ? formatter.format(parsedAmount, outgoingAsset.toUpperCase())
          : formatter.format(parsedAmount, incomingAsset.toUpperCase()),
      serviceFee,
      formattedServiceFee: formatAsset(serviceFee, incomingAsset),
      feePercentage,
      totalAmount,
      formattedTotalAmount:
        tradeType === "buy"
          ? formatter.format(totalAmount, incomingAsset.toUpperCase())
          : formatter.format(totalAmount, outgoingAsset.toUpperCase()),
      pair,
      fulfilledAmount: 0,
      fulfilledFormatted: formatAsset(0, incomingAsset),
      awaitingFulfillmentAmount: 0,
      awaitingFulfillmentFormatted: formatAsset(0, incomingAsset),
      settledAmount: 0,
      settledFormatted: formatCurrency(0, outgoingAsset),
    };
  };

  return { getQuote };
};
